import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'

import { danger, info, primary, success, surface, surfaceDark, warning } from './palettes'

/**
 * Aura re-tokened to the legacy Vuexy look (D-06, ARCHITECTURE.md §5). Values come from the legacy SCSS; the
 * mapping, with the legacy variable of each token, is in ./README.md. Rem values assume the legacy root font size
 * of 14px (`html { font-size: 14px }`, set in ./tailwind.css).
 */
export const SkileoPreset = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '0.12rem', // $border-radius-xs
      sm: '0.25rem', // $border-radius-sm
      // Raised from legacy's 0.357 / 0.428 / 0.6rem (5 / 6 / 8.4px) to 6 / 10 / 12px for the floating nav redesign;
      // the components that hard-coded those legacy values read these tokens instead (`--p-border-radius-*`).
      md: '0.428rem', // $border-radius (inputs, buttons, tags)
      lg: '0.714rem', // $card-border-radius (cards, navbar, content frames)
      xl: '0.857rem', // $border-radius-lg (nav panel, popovers, modals)
    },
    // Aura reads the severities from these primitives (message/toast: green, blue, yellow, red;
    // tag/badge: green, sky, orange, red); Vuexy has one colour per severity.
    green: success,
    blue: info,
    sky: info,
    yellow: warning,
    orange: warning,
    red: danger,
  },
  semantic: {
    primary,
    focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'none' },
    // The search field at the top of a select / multi-select / listbox panel: the same inset on the sides as on top
    // (Aura: 0.5rem 1rem 0.25rem — the field sat twice as far from the sides as from the top).
    list: { header: { padding: '0.5rem 0.5rem 0.25rem 0.5rem' } },
    formField: {
      paddingX: '1rem', // $input-padding-x
      paddingY: '0.438rem', // $input-padding-y
      sm: { fontSize: '0.857rem', paddingX: '0.857rem', paddingY: '0.188rem' }, // $input-*-sm
      lg: { fontSize: '1.143rem', paddingX: '1.143rem', paddingY: '0.75rem' }, // $input-*-lg
      borderRadius: '{border.radius.md}',
    },
    colorScheme: {
      light: {
        surface,
        primary: {
          color: '{primary.500}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}',
        },
        formField: {
          disabledBackground: '#efefef', // $input-disabled-bg
          shadow: 'none',
          focusBorderColor: '{primary.color}', // $input-focus-border-color
        },
      },
      /**
       * The dark scheme (2026-10, the navbar's switch; legacy had none). Aura's own dark scheme keeps a light-first
       * surface scale and points its semantics at the far end (text {surface.0}, content {surface.900}); here the scale
       * itself is dark-first (`surfaceDark`) and the semantics are the light ones, so the shell's direct `--p-surface-*`
       * use (cards 0, page 50, borders 200, text 700…) comes out right without a second set of rules. Component-level
       * dark tokens of Aura still reference the light-first order: a component that looks off in dark gets its own
       * `colorScheme.dark` entry below.
       */
      dark: {
        surface: surfaceDark,
        primary: {
          color: '{primary.400}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.300}',
          activeColor: '{primary.200}',
        },
        highlight: {
          background: 'color-mix(in srgb, {primary.400}, transparent 80%)',
          focusBackground: 'color-mix(in srgb, {primary.400}, transparent 70%)',
          color: '{primary.200}',
          focusColor: '{primary.100}',
        },
        mask: { background: 'rgba(0, 0, 0, 0.6)', color: '{surface.700}' },
        formField: {
          background: '{surface.0}',
          disabledBackground: '{surface.100}',
          filledBackground: '{surface.50}',
          filledHoverBackground: '{surface.50}',
          filledFocusBackground: '{surface.50}',
          borderColor: '{surface.300}',
          hoverBorderColor: '{surface.400}',
          focusBorderColor: '{primary.color}',
          invalidBorderColor: '{red.400}',
          color: '{surface.800}',
          disabledColor: '{surface.500}',
          placeholderColor: '{surface.500}',
          invalidPlaceholderColor: '{red.400}',
          floatLabelColor: '{surface.500}',
          floatLabelFocusColor: '{primary.color}',
          floatLabelActiveColor: '{surface.500}',
          floatLabelInvalidColor: '{form.field.invalid.placeholder.color}',
          iconColor: '{surface.400}',
          shadow: 'none',
        },
        text: {
          color: '{surface.700}',
          hoverColor: '{surface.800}',
          mutedColor: '{surface.500}',
          hoverMutedColor: '{surface.600}',
        },
        content: {
          background: '{surface.0}',
          hoverBackground: '{surface.100}',
          borderColor: '{surface.200}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}',
        },
        overlay: {
          select: { background: '{surface.0}', borderColor: '{surface.200}', color: '{text.color}' },
          popover: { background: '{surface.0}', borderColor: '{surface.200}', color: '{text.color}' },
          modal: { background: '{surface.0}', borderColor: '{surface.200}', color: '{text.color}' },
        },
        list: {
          option: {
            focusBackground: '{surface.100}',
            selectedBackground: '{highlight.background}',
            selectedFocusBackground: '{highlight.focus.background}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            selectedColor: '{highlight.color}',
            selectedFocusColor: '{highlight.focus.color}',
            icon: { color: '{surface.400}', focusColor: '{surface.500}' },
          },
          optionGroup: { background: 'transparent', color: '{text.muted.color}' },
        },
        navigation: {
          item: {
            focusBackground: '{surface.100}',
            activeBackground: '{surface.100}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            activeColor: '{text.hover.color}',
            icon: { color: '{surface.400}', focusColor: '{surface.500}', activeColor: '{surface.500}' },
          },
          submenuLabel: { background: 'transparent', color: '{text.muted.color}' },
          submenuIcon: { color: '{surface.400}', focusColor: '{surface.500}', activeColor: '{surface.500}' },
        },
      },
    },
  },
  components: {
    /*
     * Buttons are pills (2026-10, the owner's call after the top bar and the list header): 40 px tall by default
     * (20.3 px line + 2 × 8.85 px + the 1 px borders), 32 px small, icon-only ones round. Buttons glued to another
     * control (input add-ons, button groups) keep the medium radius — tailwind.css, @layer components.
     */
    button: {
      root: {
        paddingX: '1.5rem', // $btn-padding-x
        paddingY: '0.632rem',
        borderRadius: '10rem',
        iconOnlyWidth: '2.857rem',
        sm: { fontSize: '0.9rem', paddingX: '1rem', paddingY: '0.418rem' },
        lg: { fontSize: '1.25rem', paddingX: '2rem', paddingY: '0.8rem' }, // $input-btn-*-lg
        label: { fontWeight: '500' }, // $font-weight-bold
      },
    },
    card: {
      root: {
        borderRadius: '{border.radius.lg}', // $card-border-radius
        shadow: '0 4px 24px 0 rgba(34, 41, 47, 0.1)', // $box-shadow
      },
      body: { padding: '1.5rem' }, // $card-spacer-x / -y
    },
    tooltip: {
      root: { maxWidth: '220px', padding: '0.4rem 0.775rem', borderRadius: '{border.radius.md}' }, // $tooltip-*
      colorScheme: {
        light: { root: { background: '#323232', color: '#ffffff' } }, // $tooltip-bg
        dark: { root: { background: '{surface.800}', color: '{surface.50}' } },
      },
    },
  },
})
